import type { FC, PropsWithChildren } from 'react'
import { useContext } from 'react'

import { VideoFeedJsonLd } from '@core/components/VideoFeedJsonLd'
import type { IAllWidgetParams } from '@core/types'
import { useWidgetProductsHydration } from '@core/widgets/hooks/useWidgetProductsHydration'

import { FeedQueryContext, HydratedFeedContext } from './contexts'
import type { FeedSourceConfig } from './types'
import { useFeedQuery } from './useFeedQuery'

interface Props {
  configs: FeedSourceConfig[]
  params: IAllWidgetParams
}

/**
 * HOC to provide embed feed context.
 */
export const FeedQueryWrapper: FC<PropsWithChildren<Props>> = ({
  configs,
  children,
  params,
}) => {
  // Top level component may pass a hydrated feed.
  // i.e We can pass a feed to programmatic player, which will use it instead of
  // fetching it from the server. This is useful for headless feed use case. We
  // want to pass the feed in the headless feed to the programmatic player.
  const hydratedFeed = useContext(HydratedFeedContext)
  const feedQuery = useFeedQuery({
    config: configs,
    params,
    feed: hydratedFeed,
  })

  useWidgetProductsHydration({ videos: feedQuery[0].videos, params })

  return (
    <FeedQueryContext.Provider value={feedQuery}>
      {params.jsonld && feedQuery[0].videos.length > 0 ? (
        <VideoFeedJsonLd feedVideos={feedQuery[0].videos} />
      ) : null}

      {children}
    </FeedQueryContext.Provider>
  )
}
